import React, { useState, useMemo, useEffect } from 'react';

// Component imports
import HearingsTabContent from './HearingsTabContent';
import MinutesTabContent from './MinutesTabContent';
import EvidenceTabContent from './EvidenceTabContent';

// Animation styles
const animationStyles = `
  @keyframes fade-in-down {
    0% { opacity: 0; transform: translateY(-20px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  .animate-fade-in-down {
    animation: fade-in-down 0.3s ease-out forwards;
  }
`;
import {
  XMarkIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserCircleIcon,
  PhoneIcon,
  HomeIcon,
  ClipboardDocumentCheckIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  BellIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  PaperClipIcon,
  ClipboardIcon,
  ScaleIcon,
  ClipboardDocumentListIcon,
  DocumentPlusIcon,
  PencilSquareIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  InformationCircleIcon,
  BookOpenIcon,
  DocumentDuplicateIcon,
  ChevronRightIcon,
  ChatBubbleBottomCenterTextIcon,
  DocumentMagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  AtSymbolIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import NotificationService from '../../services/NotificationService';
import {
  showSuccess,
  showError,
  showWarning,
} from '../../utils/notificationHelper';
import { disputesAPI } from '../../services/api';
import LuponMembersManagement from './LuponMembersManagement';
import { useFetch } from '../../hooks/useCallApi';
import { useMutation } from 'react-query';

// Component to display detailed dispute information in a modal interface
// Follows ReactJS coding standards with optimized rendering and consistent formatting

const DisputeDetailsModal = ({ dispute, onClose, onUpdate }) => {
  // State to track which content tab is currently active
  const [activeTab, setActiveTab] = useState('details');

  const [disputeData, setDisputeData] = useState(dispute);

  const [complianceReportDownloading, setComplianceReportDownloading] =
    useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setDisputeData(dispute);
  }, [dispute]);

  // Modal visibility states for sub-modals
  const [showScheduleHearingModal, setShowScheduleHearingModal] =
    useState(false);
  const [showAddEvidenceModal, setShowAddEvidenceModal] = useState(false);
  const [showAddResolutionModal, setShowAddResolutionModal] = useState(false);

  // Generate default values for hearing form
  const generateDefaultHearingValues = () => {
    // Get current date in YYYY-MM-DD format for the date input
    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];

    // Get current time in HH:MM format for the time input
    // Adding 1 hour to current time for reasonable scheduling
    today.setHours(today.getHours() + 1);
    const hours = String(today.getHours()).padStart(2, '0');
    const minutes = String(today.getMinutes()).padStart(2, '0');
    const formattedTime = `${hours}:${minutes}`;

    // Default location
    const defaultLocation = 'Barangay Hall';

    // Generate a summary of the hearing details for the custom message
    const hearingDate = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(today);

    const hearingTime = `${today.getHours() % 12 || 12}:${minutes} ${
      today.getHours() >= 12 ? 'PM' : 'AM'
    }`;

    const customMessage = `You are required to attend a hearing for case ${
      dispute?.case_number || ''
    } on ${hearingDate} at ${hearingTime} at the ${defaultLocation}. Please arrive 15 minutes early and bring all relevant documents.`;

    return {
      hearing_date: formattedDate,
      hearing_time: formattedTime,
      location: defaultLocation,
      notification_type: 'both',
      notes: '',
      custom_message: customMessage,
      notify_complainant: true,
      notify_respondent: true,
    };
  };

  // Form states for various actions
  const [hearingFormData, setHearingFormData] = useState(
    generateDefaultHearingValues()
  );

  const [evidenceFormData, setEvidenceFormData] = useState({
    title: '',
    description: '',
    submitted_by: 'complainant',
    evidence_file: null,
  });

  const [resolutionFormData, setResolutionFormData] = useState({
    type: 'settlement',
    date: new Date().toISOString().split('T')[0],
    details: '',
    terms: '',
    document_file: null,
  });

  // Notes form state is now handled in MinutesTabContent.jsx

  const [errors, setErrors] = useState({});

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';

    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  // Format time for display in 12-hour format with AM/PM
  const formatTime = (timeString) => {
    if (!timeString) return 'Not specified';

    // Parse time string (expects "HH:mm" or "HH:mm:ss")
    const [hourStr, minuteStr = '00'] = timeString.split(':');
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr, 10);

    // Calculate 12-hour format
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;

    // Pad minutes to 2 digits
    const formattedMinute = minute.toString().padStart(2, '0');

    return `${hour12}:${formattedMinute} ${period}`;
  };

  const getFileNameFromUrlOrPath = (value) => {
    if (!value || typeof value !== 'string') return 'Attachment';
    const cleaned = value.split('?')[0];
    const parts = cleaned.split('/');
    return parts[parts.length - 1] || 'Attachment';
  };

  const getFileTypeLabelFromName = (fileName) => {
    if (!fileName || typeof fileName !== 'string') return 'Document';
    const ext = fileName.toLowerCase().split('.').pop();
    switch (ext) {
      case 'pdf':
        return 'PDF Document';
      case 'doc':
      case 'docx':
        return 'Word Document';
      case 'xls':
      case 'xlsx':
        return 'Excel Document';
      case 'ppt':
      case 'pptx':
        return 'PowerPoint Document';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
      case 'bmp':
      case 'svg':
        return 'Image';
      default:
        return 'Document';
    }
  };

  const getFilenameFromContentDisposition = (contentDisposition) => {
    if (!contentDisposition || typeof contentDisposition !== 'string')
      return null;

    const match = contentDisposition.match(
      /filename\*?=(?:UTF-8''|\")?([^;\"\n]+)\"?/i
    );
    if (!match || !match[1]) return null;

    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  };

  const handleDownloadComplianceReport = async () => {
    if (!dispute?.id) return;
    setComplianceReportDownloading(true);

    try {
      const response = await disputesAPI.downloadComplianceReport(dispute.id);

      const contentDisposition =
        response?.headers?.['content-disposition'] ||
        response?.headers?.['Content-Disposition'];
      const filenameFromHeader =
        getFilenameFromContentDisposition(contentDisposition);
      const fallbackName = `${
        dispute.case_number || `dispute-${dispute.id}`
      }-compliance-report.pdf`;

      const blob =
        response?.data instanceof Blob
          ? response.data
          : new Blob([response?.data]);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filenameFromHeader || fallbackName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading compliance report:', error);
      showError('Failed to download compliance report');
    } finally {
      setComplianceReportDownloading(false);
    }
  };

  // Get status badge style with enhanced visual design
  const getStatusBadgeClass = (status) => {
    const statusMap = {
      pending: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      scheduled: 'bg-blue-100 text-blue-800 border border-blue-200',
      settled: 'bg-green-100 text-green-800 border border-green-200',
      escalated: 'bg-red-100 text-red-800 border border-red-200',
    };

    // Return consistent badge styling based on status with fallback for unknown status
    return `px-3 py-1 rounded-full text-xs font-medium ${
      statusMap[status] || 'bg-gray-100 text-gray-800 border border-gray-200'
    }`;
  };

  // Get status icon based on current status
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return (
          <ExclamationCircleIcon className='h-4 w-4 text-yellow-500 mr-1' />
        );
      case 'scheduled':
        return <CalendarIcon className='h-4 w-4 text-blue-500 mr-1' />;
      case 'settled':
        return (
          <ClipboardDocumentCheckIcon className='h-4 w-4 text-green-500 mr-1' />
        );
      case 'escalated':
        return <ArrowPathIcon className='h-4 w-4 text-red-500 mr-1' />;
      default:
        return null;
    }
  };

  /**
   * Returns the appropriate icon component for a timeline event type
   * @param {string} eventType - The type of event
   * @returns {JSX.Element} - The icon component to render
   */
  const getTimelineIcon = (eventType) => {
    switch (eventType) {
      case 'filing':
        return <DocumentTextIcon className='h-5 w-5 text-purple-600' />;
      case 'summons':
        return <EnvelopeIcon className='h-5 w-5 text-amber-600' />;
      case 'hearing':
        return <ClipboardDocumentCheckIcon className='h-5 w-5 text-blue-600' />;
      case 'evidence':
        return <PaperClipIcon className='h-5 w-5 text-teal-600' />;
      case 'note':
        return <PencilSquareIcon className='h-5 w-5 text-indigo-600' />;
      case 'resolution':
        return <ScaleIcon className='h-5 w-5 text-green-600' />;
      case 'escalation':
        return <ArrowPathIcon className='h-5 w-5 text-red-600' />;
      case 'notification':
        return <BellIcon className='h-5 w-5 text-amber-600' />;
      default:
        return <InformationCircleIcon className='h-5 w-5 text-gray-600' />;
    }
  };
  console.log('dis', disputeData);
  const { data: luponData, isLoading: luponLoading } = useFetch(
    ['lupon-members', disputeData?.id],
    () => disputesAPI.getLupon(disputeData?.id),
    {
      enabled: !!disputeData?.id,
    }
  );
  const membersList = luponData?.result || [];
  // At the top of your component

  const { mutate: addLuponDispute, isLoading: isSaving } = useMutation(
    (data) => disputesAPI.addLuponDispute(disputeData.id, data),
    {
      onSuccess: () => {
        showSuccess('Lupon members saved successfully');
        handleClear();
      },
      onError: () => {
        showError('Failed to save members');
      },
    }
  );

  // Form state
  const [formData, setFormData] = useState({
    chairman: '',
    secretary: '',
    member: '',
  });

  const handleClear = () => {
    setFormData({ chairman: '', secretary: '', member: '' });
  };
  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };
  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = {
      chairman: Number(formData.chairman),
      secretary: Number(formData.secretary),
      member: Number(formData.member),
    };

    console.log('Lupon payload:', payload);

    addLuponDispute(payload);
  };

  const availableChairmen = membersList;

  const availableSecretaries = membersList.filter(
    (m) => m.resident.id !== Number(formData.chairman)
  );

  const availableMembers = membersList.filter(
    (m) =>
      m.resident.id !== Number(formData.chairman) &&
      m.resident.id !== Number(formData.secretary)
  );
  // Timeline events state
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Hearings state
  const [hearings, setHearings] = useState(dispute?.hearings || []);
  const [hearingsLoading, setHearingsLoading] = useState(false);

  // Refresh hearing form data when schedule modal is opened
  useEffect(() => {
    if (showScheduleHearingModal) {
      setHearingFormData(generateDefaultHearingValues());
    }
  }, [showScheduleHearingModal, dispute?.case_number]);

  // Ensure HearingsTabContent receives latest hearings
  useEffect(() => {
    if (dispute && dispute.hearings) {
      // Always update the hearings state when dispute.hearings changes
      // This ensures the child components see the latest data
      setHearings(dispute.hearings);
    }
  }, [dispute?.hearings]);

  // Force component update when activeTab changes to hearings
  useEffect(() => {
    if (activeTab === 'hearings' && dispute?.id) {
      // Fetch fresh data when the hearings tab is activated
      fetchDisputeData();
    }
  }, [activeTab, dispute?.id]);

  // Process timeline data from dispute details
  // This function uses the dispute data from the main API call
  const processTimelineData = (disputeData) => {
    if (!disputeData) return;

    try {
      // Generate timeline events from dispute data
      const events = [];

      // Add filing event
      // events.push({
      //   id: 'filing-' + disputeData.id,
      //   type: 'filing',
      //   title: 'Case Filed',
      //   date: new Date(disputeData.date_filed || Date.now()),
      //   description: `Case #${disputeData.case_number || 'BRG-2023-001'} was filed by ${disputeData.complainant_name || 'complainant'}`,
      //   metadata: { caseNumber: disputeData.case_number }
      // });

      // Add filing event
      if (disputeData.incident_date) {
        const incidentUTC = new Date(disputeData.incident_date);
        const incidentTime = disputeData.incident_time || '00:00:00';

        const [hour, minute, second] = incidentTime.split(':').map(Number);
        incidentUTC.setHours(hour, minute, second);

        events.push({
          id: 'filing-' + disputeData.id,
          type: 'filing',
          title: 'Case Filed',
          date: incidentUTC,
          description: `Case #${disputeData.case_number} was filed by ${disputeData.complainant_name}`,
          metadata: { caseNumber: disputeData.case_number },
        });
      }

      // Add summons event if applicable
      if (disputeData.summons_date) {
        events.push({
          id: 'summons-' + disputeData.id,
          type: 'summons',
          title: 'Summons Issued',
          date: new Date(disputeData.summons_date),
          description: `Summons was issued to respondent ${
            disputeData.respondent_name || 'respondent'
          }`,
          metadata: { issuedBy: 'Barangay Secretary' },
        });
      }

      // Add hearing events
      // if (disputeData.hearings && disputeData.hearings.length > 0) {
      //   disputeData.hearings.forEach((hearing, index) => {
      //     events.push({
      //       id: 'hearing-' + hearing.id,
      //       type: 'hearing',
      //       title: `${index === 0 ? 'Initial' : 'Follow-up'} Hearing ${index + 1}`,
      //       date: new Date(hearing.hearing_date),
      //       description: `${index === 0 ? 'First' : 'Follow-up'} mediation hearing ${hearing.status === 'completed' ? 'was conducted' : 'scheduled'}`,
      //       metadata: {
      //         location: hearing.location || 'Barangay Hall',
      //         status: hearing.status || 'scheduled',
      //         time: hearing.hearing_time
      //       }
      //     });
      //   });
      // }

      if (disputeData.hearings && disputeData.hearings.length > 0) {
        // Step 1: sort ascending to assign Initial/Follow-up correctly
        const hearingsByCreation = [...disputeData.hearings].sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at)
        );

        // Step 2: map with proper labels
        const hearingsWithLabels = hearingsByCreation.map((hearing, index) => ({
          ...hearing,
          label: index === 0 ? 'Initial' : 'Follow-up',
        }));

        // Step 3: sort descending for timeline display (latest created at top)
        const sortedForDisplay = hearingsWithLabels.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        // Step 4: push to events
        sortedForDisplay.forEach((hearing) => {
          const createdAt = new Date(hearing.created_at);

          events.push({
            id: 'hearing-' + hearing.id,
            type: 'hearing',
            title: `${hearing.label} Hearing`,
            date: createdAt, // only show created_at
            description: `${hearing.label} mediation hearing ${
              hearing.status === 'completed' ? 'was conducted' : 'scheduled'
            }`,
            metadata: {
              location: hearing.location || 'Barangay Hall',
              status: hearing.status || 'scheduled',
              created_at: hearing.created_at,
              scheduled_time: hearing.hearing_time,
            },
          });
        });
      }

      // Add evidence events if applicable
      if (disputeData.evidence_files && disputeData.evidence_files.length > 0) {
        disputeData.evidence_files.forEach((evidence) => {
          events.push({
            id: 'evidence-' + evidence.id,
            type: 'evidence',
            title: 'Evidence Submitted',
            date: new Date(evidence.created_at),
            description:
              evidence.description || 'Supporting documents were submitted',
            metadata: {
              submittedBy: evidence.submitted_by || 'party',
              evidenceType: evidence.type || 'document',
            },
          });
        });
      }

      // Add notes/minutes events if applicable
      if (disputeData.notes && disputeData.notes.length > 0) {
        disputeData.notes.forEach((note) => {
          events.push({
            id: 'note-' + note.id,
            type: 'note',
            title:
              note.note_type === 'meeting_minutes'
                ? 'Meeting Minutes Added'
                : 'Case Note Added',
            date: new Date(note.created_at),
            description: note.content
              ? note.content.substring(0, 100) + '...'
              : 'Notes were recorded',
            metadata: {
              author: note.created_by || 'Barangay Secretary',
              noteType: note.note_type || 'note',
            },
          });
        });
      }

      // Add resolution event if case is settled
      if (disputeData.status === 'settled' && disputeData.resolution) {
        events.push({
          id: 'resolution-' + disputeData.id,
          type: 'resolution',
          title: 'Case Resolved',
          date: new Date(
            disputeData.resolution.resolution_date || disputeData.updated_at
          ),
          description:
            disputeData.resolution.details ||
            'Parties reached an amicable settlement',
          metadata: {
            resolutionType:
              disputeData.resolution.resolution_type || 'settlement',
            documentNumber:
              disputeData.resolution.document_number ||
              `SET-${disputeData.case_number}`,
          },
        });
      }

      // Sort events by date
      events.sort((a, b) => new Date(a.date) - new Date(b.date));

      // Update timeline events
      setTimelineEvents(events);
    } catch (error) {
      console.error('Error processing timeline data:', error);
      // Set fallback initial event if timeline generation fails
      setTimelineEvents([
        {
          id: 'filing-default',
          type: 'filing',
          title: 'Case Filed',
          date: new Date(dispute?.date_filed || '2023-11-15'),
          description: `Case #${
            dispute?.case_number || 'BRG-2023-001'
          } was filed by ${dispute?.complainant_name || 'complainant'}`,
          metadata: { caseNumber: dispute?.case_number },
        },
      ]);
    } finally {
      setTimelineLoading(false);
    }
  };

  // Unified fetch function for all tabs
  const fetchTimelineData = async () => {
    const disputeData = await fetchDisputeData();
    if (disputeData) {
      processTimelineData(disputeData);
    }
  };

  // Unified data fetching for all tabs to eliminate redundant API calls

  // Create a data cache object to track when each data type was last fetched
  const [dataCache, setDataCache] = useState({
    lastFetched: null,
    tabs: {
      hearings: false,
      minutes: false,
      evidence: false,
      resolutions: false,
      timeline: false,
    },
  });

  // Single useEffect for all tab content data fetching
  useEffect(() => {
    if (!dispute?.id) return;

    // Determine if we need to fetch data for the current tab
    const needsDataFetch = () => {
      // Always fetch if this tab hasn't been loaded before
      if (!dataCache.tabs[activeTab]) return true;

      // For hearings tab, check if we have data
      if (activeTab === 'hearings' && (!hearings.length || hearingsLoading))
        return true;

      // For timeline tab, we need special processing
      if (activeTab === 'timeline' && !timelineEvents.length) return true;

      // Default: no fetch needed
      return false;
    };

    // Fetch data only when needed
    const fetchTabData = async () => {
      if (!needsDataFetch()) return;

      // Use fetchDisputeData as our single source of truth
      const disputeData = await fetchDisputeData();

      if (disputeData) {
        // Mark this tab as fetched
        setDataCache((prev) => ({
          lastFetched: new Date(),
          tabs: {
            ...prev.tabs,
            [activeTab]: true,
          },
        }));

        // For timeline tab, we need additional processing
        if (activeTab === 'timeline') {
          processTimelineData(disputeData);
        }
      }
    };

    fetchTabData();
  }, [activeTab, dispute?.id]);

  // This consolidated approach eliminates redundant API calls

  // Unified function to fetch dispute data for all tabs
  const fetchDisputeData = async () => {
    if (!dispute?.id) return null;

    // Set loading state based on active tab
    if (activeTab === 'hearings') setHearingsLoading(true);

    try {
      // Single API call to get the full dispute details
      const response = await disputesAPI.getDetails(dispute.id);

      if (response.data) {
        // Update states based on active tab or the data that was requested

        // Always update hearings data since it's used in multiple tabs
        setHearings(response.data.hearings || []);

        // Create a copy of the dispute with ALL updated data from the API response
        const updatedDispute = {
          ...dispute,
          ...response.data,
          hearings: response.data.hearings || [],
          notes: response.data.notes || [],
          evidence_files: response.data.evidence_files || [],
        };

        setDisputeData(updatedDispute);

        // Update the parent component with the fresh data
        // This ensures the parent has the latest data when the modal is eventually closed
        // The second parameter (false) ensures the modal stays open

        // Return the updated data for other functions to use
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching dispute data:', error);
      showError('Failed to load case data');
      return null;
    } finally {
      if (activeTab === 'hearings') setHearingsLoading(false);
    }
  };

  // Loading states for various submissions
  const [loading, setLoading] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState(null);
  const [fileSelected, setFileSelected] = useState(null);
  const [evidenceFileSelected, setEvidenceFileSelected] = useState(null);
  const [resolutionFileSelected, setResolutionFileSelected] = useState(null);

  /**
   * Handles scheduling a new hearing
   * @param {Event} e - Form submission event
   */
  const handleScheduleHearing = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Form validation
    const newErrors = {};
    if (!hearingFormData.hearing_date)
      newErrors.hearing_date = 'Date is required';
    if (!hearingFormData.hearing_time)
      newErrors.hearing_time = 'Time is required';
    if (!hearingFormData.location) newErrors.location = 'Location is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      const hearingData = {
        dispute_id: dispute.id,
        hearing_date: hearingFormData.hearing_date,
        hearing_time: hearingFormData.hearing_time,
        location: hearingFormData.location,
        notes: hearingFormData.notes,
      };

      // Make the actual API call to save the hearing data
      await disputesAPI.scheduleHearing(dispute.id, hearingData);

      console.log('Hearing scheduled successfully:', hearingData);

      // Send notifications if requested
      if (hearingFormData.notification_type !== 'none') {
        await sendNotifications(hearingData);
      }

      // Show success toast notification
      showSuccess('Hearing scheduled successfully!');

      // Close the schedule hearing modal only after successful operation
      setShowScheduleHearingModal(false);

      // Refresh complete dispute data with a single API call
      // This will update hearings and other data needed by child components
      const updatedData = await fetchDisputeData();

      // If we're on the timeline tab, also update timeline with the fresh data
      if (activeTab === 'timeline') {
        await fetchTimelineData();
      }

      // Notify parent component to refresh disputes list and statistics
      if (onUpdate) {
        onUpdate();
      }

      // Reset form data with fresh defaults
      setHearingFormData(generateDefaultHearingValues());
    } catch (error) {
      console.error('Error scheduling hearing:', error);
      showError('Error scheduling hearing: ' + error.message);
      setErrors({ form: 'Failed to schedule hearing. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sends notifications for the scheduled hearing
   * @param {Object} hearingData - The scheduled hearing data
   * @returns {Promise} - Result of the notification sending
   */
  const sendNotifications = async (hearingData) => {
    if (hearingFormData.notification_type === 'none') {
      return { success: true, message: 'No notifications requested' };
    }

    try {
      setNotificationStatus({ sending: true });

      // Prepare notification data
      const notificationData = {
        disputeId: dispute.id,
        notificationType: hearingFormData.notification_type,
        hearingDate: hearingFormData.hearing_date,
        hearingTime: hearingFormData.hearing_time,
        location: hearingFormData.location,
        additionalNotes: hearingFormData.notes,
        customMessage: hearingFormData.custom_message,
        recipients: {
          notifyComplainant: hearingFormData.notify_complainant,
          notifyRespondent: hearingFormData.notify_respondent,
          complainant: {
            name: dispute.complainant_name,
            email: dispute.complainant_email,
            phone: dispute.complainant_phone,
          },
          respondent: {
            name: dispute.respondent_name,
            email: dispute.respondent_email,
            phone: dispute.respondent_phone,
          },
        },
      };

      // Send the notifications using the real API endpoint
      const result = await NotificationService.sendHearingNotifications(
        notificationData
      );

      // Show success notification with appropriate message based on notification type
      let successMessage = '';
      switch (hearingFormData.notification_type) {
        case 'email':
          successMessage = 'Email notifications sent successfully';
          break;
        case 'sms':
          successMessage = 'SMS notifications sent successfully';
          break;
        case 'both':
          successMessage = 'Email and SMS notifications sent successfully';
          break;
        default:
          successMessage = 'Notifications sent successfully';
      }

      showSuccess(successMessage);

      setNotificationStatus({
        success: true,
        message: successMessage,
        details: result,
      });

      return result;
    } catch (error) {
      console.error('Error sending notifications:', error);

      // Show error notification with more specific message
      const errorMessage =
        'Failed to send notifications. The hearing was scheduled, but participants will need to be notified manually.';
      showWarning(errorMessage);

      setNotificationStatus({
        success: false,
        message: errorMessage,
        error: error.message || 'Network error occurred',
      });

      // Don't block the hearing creation due to notification failure
      return {
        success: false,
        error: error.message || 'Network error occurred',
      };
    }
  };

  /**
   * Handles submitting new evidence
   * @param {Event} e - Form submission event
   */
  const handleSubmitEvidence = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate form
    const newErrors = {};
    if (!evidenceFormData.title) newErrors.title = 'Title is required';
    if (!evidenceFormData.description)
      newErrors.description = 'Description is required';
    if (!evidenceFileSelected)
      newErrors.evidence_file = 'Evidence file is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      // Prepare form data with files
      const submitData = new FormData();

      // Add all form fields
      for (const key in evidenceFormData) {
        if (key !== 'evidence_file') {
          submitData.append(key, evidenceFormData[key]);
        }
      }

      // Add evidence file if selected
      if (evidenceFileSelected) {
        submitData.append('evidence_file', evidenceFileSelected);
      }

      // Make the actual API call to save the evidence
      await disputesAPI.uploadEvidence(dispute.id, submitData);

      console.log('Evidence added successfully');

      // Show success toast notification
      showSuccess('Evidence submitted successfully!');

      // Close the evidence modal
      setShowAddEvidenceModal(false);

      // Refresh complete dispute data with a single API call
      const updatedData = await fetchDisputeData();

      // If we're on the timeline tab, also update timeline with the fresh data
      if (activeTab === 'timeline') {
        await fetchTimelineData();
      }

      // Reset form data
      setEvidenceFormData({
        title: '',
        description: '',
        submitted_by: 'complainant',
        evidence_file: null,
      });
      setEvidenceFileSelected(null);
      setErrors({});
    } catch (error) {
      console.error('Error submitting evidence:', error);
      showError('Error submitting evidence: ' + error.message);
      setErrors({ form: 'Failed to submit evidence. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles submitting case resolution
   * @param {Event} e - Form submission event
   */
  const handleSubmitResolution = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate form
    const newErrors = {};
    if (!resolutionFormData.type)
      newErrors.type = 'Resolution type is required';
    if (!resolutionFormData.date)
      newErrors.date = 'Resolution date is required';
    if (!resolutionFormData.details)
      newErrors.details = 'Resolution details are required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      // Prepare form data with files
      const submitData = new FormData();

      // Add all form fields
      for (const key in resolutionFormData) {
        if (key !== 'document_file') {
          submitData.append(key, resolutionFormData[key]);
        }
      }

      // Add resolution file if selected
      if (resolutionFileSelected) {
        submitData.append('document_file', resolutionFileSelected);
      }

      // Make actual API call to save the resolution
      await disputesAPI.recordResolution(dispute.id, submitData);

      console.log('Resolution submitted successfully');

      // Show success toast notification
      showSuccess('Resolution submitted successfully!');

      // Close the resolution modal
      setShowAddResolutionModal(false);

      // Refresh complete dispute data with a single API call
      const updatedData = await fetchDisputeData();

      // If we're on the timeline tab, also update timeline with the fresh data
      if (activeTab === 'timeline') {
        await fetchTimelineData();
      }

      // Notify parent component to refresh disputes list and statistics
      if (onUpdate) {
        onUpdate();
      }

      // Reset form data
      setResolutionFormData({
        type: 'settlement',
        date: new Date().toISOString().split('T')[0],
        details: '',
        terms: '',
        document_file: null,
      });
      setResolutionFileSelected(null);
      setErrors({});
    } catch (error) {
      console.error('Error submitting resolution:', error);
      showError('Error submitting resolution: ' + error.message);
      setErrors({ form: 'Failed to submit resolution. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles submitting case notes/minutes
   * @param {Event} e - Form submission event
   */
  const handleSubmitNotes = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate form
    const newErrors = {};
    if (!notesFormData.content) newErrors.content = 'Content is required';
    if (
      notesFormData.note_type === 'meeting_minutes' &&
      !notesFormData.meeting_date
    ) {
      newErrors.meeting_date = 'Meeting date is required for minutes';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      // Prepare form data with files
      const submitData = new FormData();

      // Add all form fields
      for (const key in notesFormData) {
        if (key !== 'attachment_file') {
          submitData.append(
            key,
            notesFormData[key] === null ? '' : notesFormData[key]
          );
        }
      }

      // Add attachment file if selected
      if (fileSelected) {
        submitData.append('attachment_file', fileSelected);
      }

      // Make the actual API call to save the notes
      await disputesAPI.addNotes(dispute.id, submitData);

      console.log('Notes added successfully');

      // Show success toast notification
      showSuccess('Notes submitted successfully!');

      // Close the notes modal
      setShowAddNotesModal(false);

      // Refresh complete dispute data with a single API call
      const updatedData = await fetchDisputeData();

      // If we're on the timeline tab, also update timeline with the fresh data
      if (activeTab === 'timeline') {
        await fetchTimelineData();
      }

      // Notify parent component to refresh disputes list and statistics
      if (onUpdate) {
        onUpdate();
      }

      // Reset form data
      setNotesFormData({
        note_type: 'meeting_minutes',
        content: '',
        meeting_date: new Date().toISOString().split('T')[0],
        attachment_file: null,
      });
      setFileSelected(null);
      setErrors({});
    } catch (error) {
      console.error('Error submitting notes:', error);
      showError('Error submitting notes: ' + error.message);
      setErrors({ form: 'Failed to add notes. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Form input change handlers for various forms
  const handleHearingFormChange = (e) => {
    const { name, value } = e.target;
    setHearingFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleEvidenceFormChange = (e) => {
    const { name, value } = e.target;
    setEvidenceFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleResolutionFormChange = (e) => {
    const { name, value } = e.target;
    setResolutionFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Notes handling is now done in MinutesTabContent.jsx

  // File input change handlers
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFileSelected(e.target.files[0]);
      setNotesFormData((prev) => ({
        ...prev,
        attachment_file: e.target.files[0],
      }));
    }
  };

  const handleEvidenceFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setEvidenceFileSelected(e.target.files[0]);
      setEvidenceFormData((prev) => ({
        ...prev,
        evidence_file: e.target.files[0],
      }));
    }
  };

  const handleResolutionFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setResolutionFileSelected(e.target.files[0]);
      setResolutionFormData((prev) => ({
        ...prev,
        document_file: e.target.files[0],
      }));
    }
  };

  // File removal handlers
  const handleRemoveFile = () => {
    setFileSelected(null);
    setNotesFormData((prev) => ({ ...prev, attachment_file: null }));
  };

  const handleRemoveEvidenceFile = () => {
    setEvidenceFileSelected(null);
    setEvidenceFormData((prev) => ({ ...prev, evidence_file: null }));
  };

  const handleRemoveResolutionFile = () => {
    setResolutionFileSelected(null);
    setResolutionFormData((prev) => ({ ...prev, document_file: null }));
  };

  // Define tab configuration for reusability and consistency
  // Using useMemo to prevent unnecessary re-renders when component updates
  const tabConfig = useMemo(
    () => [
      {
        id: 'details',
        label: 'Case Details',
        icon: <UserCircleIcon className='h-5 w-5' />,
      },
      {
        id: 'hearings',
        label: 'Hearings',
        icon: <CalendarIcon className='h-5 w-5' />,
      },
      {
        id: 'minutes',
        label: 'Notes',
        icon: <DocumentTextIcon className='h-5 w-5' />,
      },
      {
        id: 'evidence',
        label: 'Evidence',
        icon: <ClipboardDocumentCheckIcon className='h-5 w-5' />,
      },
      {
        id: 'resolutions',
        label: 'Resolutions',
        icon: <DocumentArrowDownIcon className='h-5 w-5' />,
      },
      {
        id: 'timeline',
        label: 'Timeline',
        icon: <ClockIcon className='h-5 w-5' />,
      },
      {
        id: 'compliance',
        label: 'Legal',
        icon: <ScaleIcon className='h-5 w-5' />,
      },
    ],
    []
  );

  // Rendering the modal with proper accessibility attributes and optimized structure
  return (
    <div
      className='fixed inset-0 bg-black bg-opacity-60 flex items-start justify-center z-50 pt-16 pb-6 px-4 overflow-y-hidden animate-fade-in-down'
      role='dialog'
      aria-modal='true'
      aria-labelledby='dispute-details-title'
    >
      {/* Style tags */}
      {/* <style>{hideScrollbarStyle}</style> */}
      <div className='bg-white rounded-lg shadow-2xl w-full max-w-5xl border border-gray-100 flex flex-col'>
        {' '}
        <div className='bg-gradient-to-r from-blue-50 to-white-100 flex justify-between items-center px-6 py-5 border-b border-gray-200 rounded-t-lg'>
          <div>
            <h2
              id='dispute-details-title'
              className='text-xl font-semibold text-gray-800 flex items-center'
            >
              <span className='bg-blue-600 text-white px-2 py-1 rounded text-sm mr-3'>
                #{dispute.case_number}
              </span>
              <span className='mr-2'>Dispute Case</span>
              <span
                className={`ml-4 flex items-center ${getStatusBadgeClass(
                  dispute.status
                )}`}
              >
                {getStatusIcon(dispute.status)}
                {dispute.status?.charAt(0).toUpperCase() +
                  dispute.status?.slice(1) || 'Unknown'}
              </span>
            </h2>
            <p className='text-sm text-gray-600 flex items-center mt-1'>
              <CalendarIcon className='h-4 w-4 text-gray-500 mr-1' />
              Filed on {formatDate(dispute.date_filed)}
            </p>
          </div>
          <button
            onClick={onClose}
            className='text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300
                       bg-white p-2 rounded-full hover:bg-gray-100 transition-all duration-200'
            aria-label='Close modal'
          >
            <XMarkIcon className='h-5 w-5' />
          </button>
        </div>
        {/* Modern Tab Navigation */}
        <div className='bg-white border-b border-gray-200 top-0 z-10 shadow-sm'>
          <nav className='flex px-4 overflow-x-auto hide-scrollbar'>
            {tabConfig.map((tab) => (
              <button
                key={tab.id}
                className={`relative py-4 px-5 text-sm font-medium flex items-center justify-center transition-all duration-200
                  ${
                    activeTab === tab.id
                      ? 'text-blue-700'
                      : 'text-gray-500 hover:text-blue-600'
                  } whitespace-nowrap`}
                onClick={() => setActiveTab(tab.id)}
                aria-selected={activeTab === tab.id}
                aria-controls={`panel-${tab.id}`}
                id={`tab-${tab.id}`}
                role='tab'
                tabIndex={activeTab === tab.id ? 0 : -1}
                aria-label={tab.label}
              >
                {/* Active Tab Indicator */}
                {activeTab === tab.id && (
                  <div className='absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-t '></div>
                )}

                {/* Tab Background Glow Effect on Active */}
                {activeTab === tab.id && (
                  <div className='absolute inset-0 bg-blue-50 opacity-0 rounded-md '></div>
                )}

                {/* Icon with Badge for Active */}
                <div
                  className={`flex items-center justify-center ${
                    activeTab === tab.id
                      ? 'text-blue-700'
                      : 'text-gray-500 group-hover:text-blue-700'
                  }`}
                >
                  <div
                    className={`p-1.5 rounded-full ${
                      activeTab === tab.id ? 'bg-blue-100' : ''
                    } mr-2`}
                  >
                    {tab.icon}
                  </div>
                  {tab.label}
                </div>
              </button>
            ))}
          </nav>
        </div>
        {/* Tab Content with proper ARIA roles for accessibility */}
        {/* <div
          className='p-6'
          role='tabpanel'
          aria-labelledby={`tab-${activeTab}`}
          id={`panel-${activeTab}`}
        > */}
        <div className='p-6 overflow-y-auto max-h-[60vh] hide-scrollbar'>
          {activeTab === 'details' && (
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              {/* Case Process Information - Enhanced Card */}
              <div className='bg-white border border-gray-200 rounded-lg p-5 shadow-sm md:col-span-2'>
                <div className='flex items-center mb-4 pb-2 border-b border-gray-100'>
                  <div className='bg-blue-100 p-2 rounded-full mr-3'>
                    <ClipboardDocumentCheckIcon className='h-6 w-6 text-blue-600' />
                  </div>
                  <h3 className='text-lg font-bold text-gray-800'>
                    Case Process
                  </h3>
                </div>

                <div className='grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4'>
                  <div className='flex items-start'>
                    <div className='bg-blue-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <ClipboardDocumentCheckIcon className='h-4 w-4 text-blue-500' />
                    </div>
                    <div>
                      <p className='text-xs text-gray-500 font-medium uppercase tracking-wide'>
                        Status
                      </p>
                      <div className='flex items-center mt-1'>
                        <span
                          className={`${getStatusBadgeClass(
                            dispute.status
                          )} flex items-center`}
                        >
                          {getStatusIcon(dispute.status)}
                          {dispute.status?.charAt(0).toUpperCase() +
                            dispute.status?.slice(1) || 'Unknown'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className='flex items-start'>
                    <div className='bg-green-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <CalendarIcon className='h-4 w-4 text-green-500' />
                    </div>
                    <div>
                      <p className='text-xs text-gray-500 font-medium uppercase tracking-wide'>
                        Next Hearing
                      </p>
                      <p className='text-base text-gray-700'>
                        {dispute.next_hearing_date
                          ? formatDate(dispute.next_hearing_date)
                          : 'Not scheduled'}
                      </p>
                    </div>
                  </div>

                  <div className='flex items-start'>
                    <div className='bg-green-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <UserCircleIcon className='h-4 w-4 text-green-500' />
                    </div>
                    <div>
                      <p className='text-xs text-gray-500 font-medium uppercase tracking-wide'>
                        Assigned Lupon Member
                      </p>
                      <p className='text-base text-gray-700'>
                        {dispute.lupon_member || 'Not yet assigned'}
                      </p>
                    </div>
                  </div>

                  {dispute.resolution_date && (
                    <div className='flex items-start'>
                      <div className='bg-green-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                        <DocumentArrowDownIcon className='h-4 w-4 text-green-500' />
                      </div>
                      <div>
                        <p className='text-xs text-gray-500 font-medium uppercase tracking-wide'>
                          Resolution Date
                        </p>
                        <p className='text-base text-gray-700'>
                          {formatDate(dispute.resolution_date)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Complainant Information - Enhanced Card */}
              <div className='bg-white border border-blue-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow duration-300'>
                <div className='flex items-center mb-4 pb-2 border-b border-gray-100'>
                  <div className='bg-blue-100 p-2 rounded-full mr-3'>
                    <UserCircleIcon className='h-6 w-6 text-blue-600' />
                  </div>
                  <h3 className='text-lg font-medium text-gray-800'>
                    Complainant
                  </h3>
                </div>

                <div className='space-y-4'>
                  <div className='flex items-start'>
                    <div className='bg-blue-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <UserCircleIcon className='h-4 w-4 text-blue-500' />
                    </div>
                    <div>
                      <p className='text-xs text-gray-500 font-medium uppercase tracking-wide'>
                        Full Name
                      </p>
                      <p className='text-base font-medium text-gray-800'>
                        {dispute.complainant_name}
                      </p>
                    </div>
                  </div>

                  <div className='flex items-start'>
                    <div className='bg-blue-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <ClipboardDocumentCheckIcon className='h-4 w-4 text-blue-500' />
                    </div>
                    <div>
                      <p className='text-xs text-gray-500 font-medium uppercase tracking-wide'>
                        Type
                      </p>
                      <p className='text-base text-gray-700 flex items-center'>
                        {dispute.complainant_type === 'resident' ? (
                          <span className='bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full border border-green-200'>
                            Resident
                          </span>
                        ) : (
                          <span className='bg-orange-100 text-orange-800 text-xs px-2 py-0.5 rounded-full border border-orange-200'>
                            Non-resident
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className='flex items-start'>
                    <div className='bg-blue-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <HomeIcon className='h-4 w-4 text-blue-500' />
                    </div>
                    <div>
                      <p className='text-xs text-gray-500 font-medium uppercase tracking-wide'>
                        Address
                      </p>
                      <p className='text-base text-gray-700'>
                        {dispute.complainant_address || 'Not provided'}
                      </p>
                    </div>
                  </div>

                  <div className='flex items-start'>
                    <div className='bg-blue-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <PhoneIcon className='h-4 w-4 text-blue-500' />
                    </div>
                    <div>
                      <p className='text-xs text-gray-500 font-medium uppercase tracking-wide'>
                        Contact
                      </p>
                      <p className='text-base text-gray-700'>
                        {dispute.complainant_contact || 'Not provided'}
                      </p>
                    </div>
                  </div>
                  <div className='flex items-start'>
                    <div className='bg-blue-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <AtSymbolIcon className='h-4 w-4 text-blue-500' />
                    </div>
                    <div>
                      <p className='text-xs text-gray-500 font-medium uppercase tracking-wide'>
                        Email Address
                      </p>
                      <p className='text-base text-gray-700'>
                        {dispute.complainant_email || 'Not provided'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Respondent Information - Enhanced Card */}
              <div className='bg-white border border-amber-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow duration-300'>
                <div className='flex items-center mb-4 pb-2 border-b border-gray-100'>
                  <div className='bg-amber-100 p-2 rounded-full mr-3'>
                    <UserCircleIcon className='h-6 w-6 text-amber-600' />
                  </div>
                  <h3 className='text-lg font-medium text-gray-800'>
                    Respondent
                  </h3>
                </div>

                <div className='space-y-4'>
                  <div className='flex items-start'>
                    <div className='bg-amber-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <UserCircleIcon className='h-4 w-4 text-amber-500' />
                    </div>
                    <div>
                      <p className='text-xs text-gray-500 font-medium uppercase tracking-wide'>
                        Full Name
                      </p>
                      <p className='text-base font-medium text-gray-800'>
                        {dispute.respondent_name}
                      </p>
                    </div>
                  </div>

                  <div className='flex items-start'>
                    <div className='bg-amber-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <ClipboardDocumentCheckIcon className='h-4 w-4 text-amber-500' />
                    </div>
                    <div>
                      <p className='text-xs text-gray-500 font-medium uppercase tracking-wide'>
                        Type
                      </p>
                      <p className='text-base text-gray-700 flex items-center'>
                        {dispute.respondent_type === 'resident' ? (
                          <span className='bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full border border-green-200'>
                            Resident
                          </span>
                        ) : (
                          <span className='bg-orange-100 text-orange-800 text-xs px-2 py-0.5 rounded-full border border-orange-200'>
                            Non-resident
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className='flex items-start'>
                    <div className='bg-amber-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <HomeIcon className='h-4 w-4 text-amber-500' />
                    </div>
                    <div>
                      <p className='text-xs text-gray-500 font-medium uppercase tracking-wide'>
                        Address
                      </p>
                      <p className='text-base text-gray-700'>
                        {dispute.respondent_address || 'Not provided'}
                      </p>
                    </div>
                  </div>

                  <div className='flex items-start'>
                    <div className='bg-amber-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <PhoneIcon className='h-4 w-4 text-amber-500' />
                    </div>
                    <div>
                      <p className='text-xs text-gray-500 font-medium uppercase tracking-wide'>
                        Contact
                      </p>
                      <p className='text-base text-gray-700'>
                        {dispute.respondent_contact || 'Not provided'}
                      </p>
                    </div>
                  </div>
                  <div className='flex items-start'>
                    <div className='bg-amber-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <AtSymbolIcon className='h-4 w-4 text-amber-500' />
                    </div>
                    <div>
                      <p className='text-xs text-gray-500 font-medium uppercase tracking-wide'>
                        Email Address
                      </p>
                      <p className='text-base text-gray-700'>
                        {dispute.respondent_email || 'Not provided'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Incident Details - Enhanced Card */}
              <div className='bg-white border border-gray-200 rounded-lg p-5 shadow-sm md:col-span-2'>
                <div className='flex items-center mb-4 pb-2 border-b border-gray-100'>
                  <div className='bg-green-100 p-2 rounded-full mr-3'>
                    <ExclamationCircleIcon className='h-6 w-6 text-green-500' />
                  </div>
                  <h3 className='text-lg font-medium text-gray-800'>
                    Incident Details
                  </h3>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-4 gap-2 mb-2'>
                  <div className='rounded-lg py-4 flex items-start'>
                    <div className='bg-green-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <CalendarIcon className='h-4 w-4 text-green-500' />
                    </div>
                    <div>
                      <p className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1'>
                        Date
                      </p>
                      <p className='text-base text-gray-800'>
                        {formatDate(dispute.incident_date)}
                      </p>
                    </div>
                  </div>

                  <div className='rounded-lg py-4 flex items-start'>
                    <div className='bg-green-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <ClockIcon className='h-4 w-4 text-green-500' />
                    </div>
                    <div>
                      <p className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1'>
                        Time
                      </p>
                      <p className='text-base text-gray-800'>
                        {formatTime(dispute.incident_time)}
                      </p>
                    </div>
                  </div>

                  <div className='rounded-lg py-4 flex items-start'>
                    <div className='bg-green-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <ClipboardDocumentCheckIcon className='h-4 w-4 text-green-500' />
                    </div>
                    <div>
                      <p className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1'>
                        Type
                      </p>
                      <p className='text-base text-gray-800'>
                        <span className='bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full text-sm'>
                          {dispute.incident_type || 'Not categorized'}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className='rounded-lg py-4 flex items-start'>
                    <div className='bg-green-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <MapPinIcon className='h-4 w-4 text-green-500' />
                    </div>
                    <div>
                      <p className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1'>
                        Location
                      </p>
                      <p className='text-base text-gray-700'>
                        {dispute.incident_location}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className='flex items-center mb-3'>
                    <div className='bg-green-50 p-1.5 rounded-full mr-3 flex-shrink-0'>
                      <DocumentArrowDownIcon className='h-4 w-4 text-green-500' />
                    </div>

                    <p className='text-xs font-medium text-gray-700 uppercase tracking-wide'>
                      Complaint Details
                    </p>
                  </div>
                  <div className=' p-5 rounded-lg border border-gray-100'>
                    <p className='text-base text-gray-700 whitespace-pre-line leading-relaxed'>
                      {dispute.complaint_details}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'hearings' && (
            <HearingsTabContent
              dispute={{ ...dispute, hearings: hearings }} // Pass the latest hearings from local state
              onRefresh={fetchDisputeData}
              onScheduleHearing={(defaultValues) => {
                if (defaultValues) {
                  // Update hearing form data with default values if provided
                  setHearingFormData(defaultValues);
                }
                setShowScheduleHearingModal(true);
              }}
            />
          )}

          {activeTab === 'minutes' && (
            <MinutesTabContent dispute={dispute} onRefresh={fetchDisputeData} />
          )}

          {activeTab === 'evidence' && (
            <EvidenceTabContent
              dispute={dispute}
              onRefresh={fetchDisputeData}
              onAddEvidence={() => setShowAddEvidenceModal(true)}
            />
          )}

          {activeTab === 'resolutions' && (
            <div className='space-y-8'>
              <div className='flex items-center justify-between mb-6 pb-2 border-b border-gray-100'>
                <div className='flex items-center'>
                  <div className='bg-green-100 p-2 rounded-full mr-3'>
                    <DocumentArrowDownIcon className='h-6 w-6 text-green-600' />
                  </div>
                  <h3 className='text-xl font-bold text-gray-800'>
                    Case Resolution
                  </h3>
                </div>

                {/* Only show the add resolution button if the case is not resolved yet */}
                {!disputeData?.resolution &&
                  disputeData?.status !== 'settled' && (
                    <button
                      type='button'
                      onClick={() => setShowAddResolutionModal(true)}
                      className='px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700
                               focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2
                               transition-all duration-200 flex items-center'
                    >
                      <DocumentArrowDownIcon className='h-5 w-5 mr-2' />
                      Add Resolution
                    </button>
                  )}
              </div>

              {!disputeData?.resolution ? (
                <div className='text-center py-12 border border-dashed border-gray-200 rounded-lg bg-gray-50'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-16 w-16 text-gray-400 mx-auto mb-3'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
                    />
                  </svg>
                  <p className='text-gray-500 text-lg font-medium'>
                    Case has not been resolved yet
                  </p>
                  <p className='text-sm text-gray-400 mt-2 max-w-md mx-auto'>
                    When a resolution is reached, details about the settlement
                    or decision will appear here
                  </p>
                </div>
              ) : (
                <div className='border border-green-100 rounded-lg p-6 bg-white'>
                  {(() => {
                    const resolution = disputeData?.resolution;

                    if (!resolution) return null;

                    const resolutionDate =
                      resolution.date || resolution.resolution_date;
                    const resolutionType =
                      resolution.type || resolution.resolution_type;
                    const baseUrl =
                      import.meta.env.VITE_API_URL?.replace('/api/v1', '') ||
                      'http://localhost:8000';
                    const resolutionDocUrl =
                      resolution.document_url ||
                      resolution.document_file_url ||
                      resolution.documentFileUrl ||
                      (resolution.document_path
                        ? resolution.document_path.startsWith('http')
                          ? resolution.document_path
                          : `${baseUrl}/storage/${resolution.document_path}`
                        : null);

                    return (
                      <>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
                          <div className='bg-green-50 rounded-lg p-4 flex items-start'>
                            <CalendarIcon className='h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0' />
                            <div>
                              <p className='text-xs font-medium text-green-700 uppercase tracking-wide mb-1'>
                                Resolution Date
                              </p>
                              <p className='text-base font-medium text-gray-800'>
                                {formatDate(resolutionDate)}
                              </p>
                            </div>
                          </div>

                          <div className='bg-green-50 rounded-lg p-4 flex items-start'>
                            <ClipboardDocumentCheckIcon className='h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0' />
                            <div>
                              <p className='text-xs font-medium text-green-700 uppercase tracking-wide mb-1'>
                                Resolution Type
                              </p>
                              <p className='text-base font-medium text-gray-800'>
                                <span className='bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm'>
                                  {resolutionType}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className='mb-6'>
                          <div className='flex items-center mb-3'>
                            <DocumentArrowDownIcon className='h-5 w-5 text-green-600 mr-2' />
                            <p className='text-xs font-medium text-green-700 uppercase tracking-wide'>
                              Resolution Details
                            </p>
                          </div>
                          <div className='bg-green-50 p-5 rounded-lg border border-green-100'>
                            <p className='text-base text-gray-700 whitespace-pre-line leading-relaxed'>
                              {resolution.details}
                            </p>
                          </div>
                        </div>

                        {resolution.terms && (
                          <div className='mb-6'>
                            <div className='flex items-center mb-3'>
                              <ClipboardDocumentCheckIcon className='h-5 w-5 text-green-600 mr-2' />
                              <p className='text-xs font-medium text-green-700 uppercase tracking-wide'>
                                Agreement Terms
                              </p>
                            </div>
                            <div className='bg-green-50 p-5 rounded-lg border border-green-100'>
                              <p className='text-base text-gray-700 whitespace-pre-line leading-relaxed'>
                                {resolution.terms}
                              </p>
                            </div>
                          </div>
                        )}

                        {resolutionDocUrl && (
                          <div className='mt-6'>
                            <div className='text-xs font-medium text-green-700 uppercase tracking-wide mb-2 flex items-center'>
                              <PaperClipIcon className='h-4 w-4 mr-2 text-green-700' />
                              Attachment
                            </div>

                            <a
                              href={resolutionDocUrl}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='flex items-center gap-4 bg-green-50 rounded-xl border border-green-200 px-4 py-3 shadow-sm hover:shadow-md hover:bg-green-100 transition-all duration-200'
                              title='View attachment'
                            >
                              <div className='h-14 w-14 rounded-xl bg-green-600 flex items-center justify-center flex-shrink-0 shadow-sm'>
                                <DocumentArrowDownIcon className='h-7 w-7 text-green-50' />
                              </div>
                              <div className='min-w-0'>
                                <div className='text-base font-semibold text-gray-900 truncate'>
                                  {getFileNameFromUrlOrPath(
                                    resolution.document_path || resolutionDocUrl
                                  )}
                                </div>
                                <div className='text-sm text-gray-600 flex items-center mt-0.5'>
                                  <span className='h-2 w-2 rounded-full bg-green-600 mr-2'></span>
                                  {getFileTypeLabelFromName(
                                    getFileNameFromUrlOrPath(
                                      resolution.document_path ||
                                        resolutionDocUrl
                                    )
                                  )}
                                </div>
                              </div>
                            </a>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              <div className='flex items-center mb-4'>
                <div className='bg-blue-100 p-2 rounded-full mr-3'>
                  <ScaleIcon className='h-6 w-6 text-blue-600' />
                </div>
                <h3 className='text-lg font-medium text-blue-800'>
                  Katarungang Pambarangay Compliance
                </h3>
              </div>
              <div className='bg-blue-50 border border-blue-200 rounded-lg p-5'>
                <p className='text-sm text-blue-800 mb-3 flex items-start'>
                  <span className='bg-blue-100 text-blue-800 rounded-full h-5 w-5 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5'>
                    §
                  </span>
                  <span>
                    <strong className='font-medium'>Legal Reference:</strong>{' '}
                    This case is being handled in accordance with the
                    Katarungang Pambarangay Law (BP 337, Sections 399–422).
                  </span>
                </p>
                <p className='text-sm text-blue-800 flex items-start'>
                  <span className='bg-blue-100 text-blue-800 rounded-full h-5 w-5 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5'>
                    ✓
                  </span>
                  <span>
                    <strong className='font-medium'>Current Status:</strong>{' '}
                    Compliant with legal requirements for barangay-level dispute
                    resolution.
                  </span>
                </p>
              </div>
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className='space-y-6'>
              <div className='flex items-center justify-between mb-6 pb-2 border-b border-gray-100'>
                <div className='flex items-center'>
                  <div className='bg-purple-100 p-2 rounded-full mr-3'>
                    <ClockIcon className='h-6 w-6 text-purple-600' />
                  </div>
                  <h3 className='text-xl font-bold text-gray-800'>
                    Case Timeline
                  </h3>
                </div>
                <div className='text-sm text-gray-500'>
                  {timelineLoading
                    ? 'Loading...'
                    : `Showing ${timelineEvents.length} events`}
                </div>
              </div>

              <div className='relative'>
                {/* Timeline line */}
                <div className='absolute left-5 top-2 bottom-2 w-0.5 bg-gray-200'></div>

                {/* Timeline events */}
                <div className='space-y-6'>
                  {timelineLoading ? (
                    <div className='flex justify-center py-8'>
                      <div className='animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600'></div>
                    </div>
                  ) : timelineEvents.length > 0 ? (
                    timelineEvents.map((event) => {
                      // Determine background color for the icon based on event type
                      const getBgColorClass = () => {
                        switch (event.type) {
                          case 'filing':
                            return 'bg-purple-100';
                          case 'summons':
                            return 'bg-amber-100';
                          case 'hearing':
                            return 'bg-blue-100';
                          case 'evidence':
                            return 'bg-teal-100';
                          case 'note':
                            return 'bg-indigo-100';
                          case 'resolution':
                            return 'bg-green-100';
                          case 'escalation':
                            return 'bg-red-100';
                          case 'notification':
                            return 'bg-yellow-100';
                          default:
                            return 'bg-gray-100';
                        }
                      };

                      return (
                        <div
                          key={event.id}
                          className='relative flex items-start group'
                        >
                          <div
                            className={`h-10 w-10 rounded-full ${getBgColorClass()} flex items-center justify-center z-10 mr-4
                                       transform transition-all duration-200 group-hover:scale-110`}
                          >
                            {getTimelineIcon(event.type)}
                          </div>
                          <div
                            className='bg-white rounded-lg border border-gray-200 p-4 flex-grow
                                       hover:shadow-md transition-shadow duration-200'
                          >
                            <div className='flex justify-between items-center mb-2'>
                              <span className='font-medium text-gray-900'>
                                {event.title}
                              </span>
                              <span className='text-sm text-gray-500'>
                                {new Date(event.date).toLocaleDateString(
                                  'en-US',
                                  {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  }
                                )}
                              </span>
                            </div>
                            <p className='text-sm text-gray-600'>
                              {event.description}
                            </p>

                            {/* Additional metadata based on event type */}
                            {event.type === 'hearing' &&
                              event.metadata?.status && (
                                <div className='mt-2 text-sm'>
                                  <span
                                    className={`${getStatusBadgeClass(
                                      event.metadata.status
                                    )} py-0.5 px-2`}
                                  >
                                    {event.metadata.status}
                                  </span>
                                  {event.metadata.location && (
                                    <span className='text-gray-500 ml-2'>
                                      <MapPinIcon className='h-4 w-4 inline mr-1' />
                                      {event.metadata.location}
                                    </span>
                                  )}
                                </div>
                              )}

                            {event.type === 'resolution' && (
                              <div className='mt-2 text-sm'>
                                <span className='bg-green-100 text-green-800 py-0.5 px-2 rounded-full'>
                                  {event.metadata?.resolutionType || 'Settled'}
                                </span>
                                {event.metadata?.documentNumber && (
                                  <span className='text-gray-500 ml-2'>
                                    <DocumentTextIcon className='h-4 w-4 inline mr-1' />
                                    Doc: {event.metadata.documentNumber}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className='text-center py-8 text-gray-500'>
                      No timeline events found for this case.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'compliance' && (
            <div className='space-y-6'>
              <div className='flex items-center mb-6 pb-2 border-b border-gray-100'>
                <div className='bg-blue-100 p-2 rounded-full mr-3'>
                  <ScaleIcon className='h-6 w-6 text-blue-600' />
                </div>
                <h3 className='text-lg font-bold text-gray-800'>
                  Katarungang Pambarangay Compliance
                </h3>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
                <div className='bg-blue-50 rounded-lg p-4 flex items-start'>
                  <BookOpenIcon className='h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0' />
                  <div>
                    <p className='text-xs font-medium text-blue-700 uppercase tracking-wide mb-1'>
                      Legal Reference
                    </p>
                    <p className='text-sm text-blue-800'>
                      BP 337, Sections 399–422
                    </p>
                    <p className='text-xs text-blue-600 mt-1'>
                      Katarungang Pambarangay Law
                    </p>
                  </div>
                </div>

                <div className='bg-blue-50 rounded-lg p-4 flex items-start'>
                  <CheckCircleIcon className='h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0' />
                  <div>
                    <p className='text-xs font-medium text-blue-700 uppercase tracking-wide mb-1'>
                      Compliance Status
                    </p>
                    <p className='text-sm font-medium text-blue-800'>
                      <span className='bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs'>
                        Compliant
                      </span>
                    </p>
                    <p className='text-xs text-blue-600 mt-1'>
                      All requirements satisfied
                    </p>
                  </div>
                </div>
              </div>

              <div className='mb-6'>
                <h4 className='text-sm font-medium text-blue-700 mb-3 flex items-center'>
                  <DocumentDuplicateIcon className='h-5 w-5 text-blue-600 mr-2' />
                  Required Documentation & Process
                </h4>

                <div className='space-y-3'>
                  <div className='flex items-center bg-white p-3 rounded-lg border border-blue-100'>
                    <span className='h-5 w-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mr-3'>
                      1
                    </span>
                    <span className='text-sm text-gray-700'>
                      Filing of complaint
                    </span>
                    <CheckCircleIcon className='h-5 w-5 text-green-600 ml-auto' />
                  </div>

                  <div className='flex items-center bg-white p-3 rounded-lg border border-blue-100'>
                    <span className='h-5 w-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mr-3'>
                      2
                    </span>
                    <span className='text-sm text-gray-700'>
                      Issuance of summons
                    </span>
                    <CheckCircleIcon className='h-5 w-5 text-green-600 ml-auto' />
                  </div>

                  <div className='flex items-center bg-white p-3 rounded-lg border border-blue-100'>
                    <span className='h-5 w-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mr-3'>
                      3
                    </span>
                    <span className='text-sm text-gray-700'>
                      Mediation proceedings
                    </span>
                    {dispute.hearings && dispute.hearings.length > 0 ? (
                      <CheckCircleIcon className='h-5 w-5 text-green-600 ml-auto' />
                    ) : (
                      <ClockIcon className='h-5 w-5 text-yellow-500 ml-auto' />
                    )}
                  </div>

                  <div className='flex items-center bg-white p-3 rounded-lg border border-blue-100'>
                    <span className='h-5 w-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mr-3'>
                      4
                    </span>
                    <span className='text-sm text-gray-700'>
                      Settlement agreement or resolution
                    </span>
                    {dispute.resolution ? (
                      <CheckCircleIcon className='h-5 w-5 text-green-600 ml-auto' />
                    ) : (
                      <ClockIcon className='h-5 w-5 text-yellow-500 ml-auto' />
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h4 className='text-sm font-medium text-blue-700 mb-3 flex items-center'>
                  <InformationCircleIcon className='h-5 w-5 text-blue-600 mr-2' />
                  Barangay Justice System Information
                </h4>

                <div className='bg-blue-50 p-4 rounded-lg border border-blue-100'>
                  <p className='text-sm text-blue-800 mb-2'>
                    <span className='font-medium'>Purpose:</span> The
                    Katarungang Pambarangay aims to promote the amicable
                    settlement of disputes at the barangay level to reduce the
                    number of court cases.
                  </p>
                  <p className='text-sm text-blue-800 mb-2'>
                    <span className='font-medium'>Jurisdiction:</span> All
                    disputes between residents of the same barangay are covered,
                    with certain exceptions as provided by law.
                  </p>
                  <p className='text-sm text-blue-800'>
                    <span className='font-medium'>Legal Effect:</span>{' '}
                    Settlements reached through this process have the same force
                    and effect as a court judgment.
                  </p>
                </div>

                <div className='mt-6 flex justify-end'>
                  <button
                    type='button'
                    onClick={handleDownloadComplianceReport}
                    disabled={complianceReportDownloading}
                    className='inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium
                              rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2
                              focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed'
                  >
                    <DocumentArrowDownIcon className='h-5 w-5 mr-2' />
                    {complianceReportDownloading
                      ? 'Downloading...'
                      : 'Download Compliance Report'}
                  </button>
                </div>
              </div>
              {/* ASSIGNED LUPON TAGAPAMAYAPA */}
              <div className='bg-white border border-gray-200 rounded-2xl p-6 shadow-sm'>
                <div className='flex items-center justify-between mb-6 pb-3 border-b border-gray-100'>
                  <div className='flex items-center'>
                    <div className='bg-amber-100 p-2 rounded-full mr-3'>
                      <UserGroupIcon className='h-6 w-6 text-amber-600' />
                    </div>
                    <h3 className='text-lg font-semibold text-gray-800'>
                      Lupon ng Tagapamayapa
                    </h3>
                  </div>

                  <button
                    type='button'
                    className='flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition'
                    onClick={() => setIsModalOpen(true)}
                  >
                    <PlusIcon className='h-4 w-4 mr-2' />
                    New Lupon Member
                  </button>
                </div>

                {/* Form Fields */}
                <form onSubmit={handleSubmit}>
                  <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                    {/* Chairman */}
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>
                        Chairman <span className='text-red-500'>*</span>
                      </label>
                      <select
                        name='chairman'
                        value={formData.chairman}
                        onChange={handleChange}
                      >
                        <option value=''>Select the chairman</option>
                        {availableChairmen.map((m) => (
                          <option key={m.id} value={m.resident.id}>
                            {m.resident.full_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Secretary */}
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>
                        Secretary <span className='text-red-500'>*</span>
                      </label>
                      <select
                        name='secretary'
                        value={formData.secretary}
                        onChange={handleChange}
                      >
                        <option value=''>Select the secretary</option>
                        {availableSecretaries.map((m) => (
                          <option key={m.id} value={m.resident.id}>
                            {m.resident.full_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Member */}
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-2'>
                        Member <span className='text-red-500'>*</span>
                      </label>
                      <select
                        name='member'
                        value={formData.member}
                        onChange={handleChange}
                      >
                        <option value=''>Select the member</option>
                        {availableMembers.map((m) => (
                          <option key={m.id} value={m.resident.id}>
                            {m.resident.full_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className='flex justify-end gap-3 mt-8 pt-4 border-t border-gray-100'>
                    <button
                      type='button'
                      onClick={handleClear}
                      className='px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition'
                    >
                      Clear
                    </button>
                    <button
                      type='submit'
                      disabled={isSaving}
                      className='px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition disabled:opacity-50'
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
        {/* Modal Footer */}
        <div className='flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-lg'>
          <button
            onClick={onClose}
            className='px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50
                      transition-all duration-200 font-medium shadow-sm hover:shadow flex items-center
                      focus:outline-none focus:ring-2 focus:ring-blue-300'
          >
            <XMarkIcon className='h-4 w-4 mr-2' />
            Close
          </button>
        </div>
        {/* Schedule Hearing Modal - Redesigned */}
        {showScheduleHearingModal && (
          <div className='fixed inset-0 bg-black bg-opacity-60 flex items-start justify-center z-50 p-4 pt-16 overflow-y-auto'>
            <div className='bg-white rounded-xl shadow-xl w-full max-w-2xl animate-fade-in-down'>
              {/* Header with gradient background */}
              <div className='bg-gradient-to-r from-blue-50 to-white-100 px-6 py-4 rounded-t-xl'>
                <div className='flex justify-between items-center'>
                  <div className='flex items-center space-x-3'>
                    <div className='bg-white bg-opacity-20 p-2 rounded-lg text-blue-600'>
                      <CalendarIcon className='h-6 w-6 ' />
                    </div>
                    <h2 className='text-xl font-semibold'>Schedule Hearing</h2>
                  </div>
                  <button
                    onClick={() => setShowScheduleHearingModal(false)}
                    className=' hover:bg-white hover:bg-opacity-20 p-1.5 rounded-full focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 transition-all duration-200'
                    aria-label='Close modal'
                  >
                    <XMarkIcon className='h-6 w-6' />
                  </button>
                </div>
                <small className=' mt-2 px-2'>
                  Case No.{' '}
                  <span className='font-medium '>{dispute.case_number}</span> •
                  &nbsp;
                  {dispute.complainant_name} vs {dispute.respondent_name}
                </small>
              </div>

              <form onSubmit={handleScheduleHearing}>
                <div className='p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4'>
                  {/* Left column */}
                  <div className='space-y-6'>
                    <div className='bg-blue-50 p-4 rounded-lg border border-blue-100'>
                      <h3 className='text-sm font-semibold text-blue-800 mb-3 flex items-center'>
                        <CalendarIcon className='h-5 w-5 mr-2 text-blue-600' />
                        Hearing Details
                      </h3>

                      <div className='space-y-4'>
                        {/* Hearing Date */}
                        <div>
                          <label className='flex items-center text-gray-700 text-sm font-medium mb-1'>
                            <span>Hearing Date</span>
                            <span className='text-red-500 ml-1'>*</span>
                          </label>
                          <div
                            className={`flex rounded-md shadow-sm overflow-hidden ${
                              errors.hearing_date ? 'ring-1 ring-red-500' : ''
                            }`}
                          >
                            <input
                              type='date'
                              name='hearing_date'
                              value={hearingFormData.hearing_date}
                              onChange={handleHearingFormChange}
                              min={new Date().toISOString().split('T')[0]}
                              className='w-full border border-gray-300 rounded-lg px-3 py-2'
                              required
                            />
                          </div>
                          {errors.hearing_date && (
                            <p className='text-red-500 text-xs mt-1'>
                              {errors.hearing_date}
                            </p>
                          )}
                        </div>

                        {/* Hearing Time */}
                        <div>
                          <label className='flex items-center text-gray-700 text-sm font-medium mb-1'>
                            <span>Hearing Time</span>
                            <span className='text-red-500 ml-1'>*</span>
                          </label>
                          <div
                            className={`flex rounded-md shadow-sm overflow-hidden ${
                              errors.hearing_time ? 'ring-1 ring-red-500' : ''
                            }`}
                          >
                            <input
                              type='time'
                              name='hearing_time'
                              value={hearingFormData.hearing_time}
                              onChange={handleHearingFormChange}
                              className='w-full border border-gray-300 rounded-lg px-3 py-2'
                              required
                            />
                          </div>
                          {errors.hearing_time && (
                            <p className='text-red-500 text-xs mt-1'>
                              {errors.hearing_time}
                            </p>
                          )}
                        </div>

                        {/* Location */}
                        <div>
                          <label className='flex items-center text-gray-700 text-sm font-medium mb-1'>
                            <span>Location</span>
                            <span className='text-red-500 ml-1'>*</span>
                          </label>
                          <div
                            className={`flex rounded-md shadow-sm overflow-hidden ${
                              errors.location ? 'ring-1 ring-red-500' : ''
                            }`}
                          >
                            <textarea
                              type='text'
                              name='location'
                              rows='3'
                              value={hearingFormData.location}
                              onChange={handleHearingFormChange}
                              className='w-full border border-gray-300 rounded-lg px-3 py-2'
                              placeholder='E.g., Barangay Hall Conference Room'
                              required
                            />
                          </div>
                          {errors.location && (
                            <p className='text-red-500 text-xs mt-1'>
                              {errors.location}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Additional Notes */}
                    <div>
                      <label className='block text-gray-700 text-sm font-medium mb-1'>
                        <span className='flex items-center'>
                          <DocumentTextIcon className='h-5 w-5 mr-1.5 text-gray-500' />
                          Additional Notes
                        </span>
                      </label>
                      <textarea
                        name='notes'
                        value={hearingFormData.notes}
                        onChange={handleHearingFormChange}
                        className='w-full border border-gray-300 rounded-lg px-3 py-2 '
                        rows='7'
                        placeholder='Any additional details about the hearing...'
                      ></textarea>
                    </div>
                  </div>

                  {/* Right column - Notification Options */}
                  <div className='space-y-6'>
                    <div className='bg-white p-4 rounded-lg border border-gray-300'>
                      <h3 className='text-sm font-semibold text-blue-800 mb-3 flex items-center'>
                        <BellIcon className='h-5 w-5 mr-2 text-blue-600' />
                        Notification Settings
                      </h3>

                      {/* Notification Methods - now with visual enhancements */}
                      <div className='mb-4'>
                        <span className='text-gray-700 text-sm font-medium mb-2'>
                          Notification Method
                        </span>
                        <div className='grid grid-cols-2 gap-3'>
                          {[
                            {
                              value: 'sms',
                              label: 'SMS',
                              icon: (
                                <PhoneIcon className='h-4 w-4 text-blue-500' />
                              ),
                            },
                            {
                              value: 'email',
                              label: 'Email',
                              icon: (
                                <EnvelopeIcon className='h-4 w-4 text-blue-500' />
                              ),
                            },
                            {
                              value: 'both',
                              label: 'Both',
                              icon: (
                                <BellIcon className='h-4 w-4 text-blue-500' />
                              ),
                            },
                            {
                              value: 'none',
                              label: 'None',
                              icon: (
                                <XMarkIcon className='h-4 w-4 text-gray-500' />
                              ),
                            },
                          ].map((option) => (
                            <label
                              key={option.value}
                              className={`flex items-center px-3 py-2 rounded-md cursor-pointer transition-all duration-200 border ${
                                hearingFormData.notification_type ===
                                option.value
                                  ? 'bg-blue-100 border-blue-300'
                                  : 'bg-white border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type='radio'
                                name='notification_type'
                                value={option.value}
                                checked={
                                  hearingFormData.notification_type ===
                                  option.value
                                }
                                onChange={handleHearingFormChange}
                                className='h-4 w-4 text-blue-600 focus:ring-blue-500'
                              />
                              <div className='ml-3 flex items-center'>
                                <span className='flex items-center justify-center h-6 w-6 rounded-full bg-white shadow-sm mr-2'>
                                  {option.icon}
                                </span>
                                <span
                                  className={`text-sm ${
                                    hearingFormData.notification_type ===
                                    option.value
                                      ? 'font-medium text-blue-800'
                                      : 'text-gray-700'
                                  }`}
                                >
                                  {option.label}
                                </span>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Notification Recipients */}
                      {hearingFormData.notification_type !== 'none' && (
                        <div className='space-y-3'>
                          <span className='text-gray-700 text-sm font-medium mb-1'>
                            Recipients
                          </span>

                          <div className='grid grid-cols-1 gap-3'>
                            <label
                              className={`flex items-center p-3 rounded-md cursor-pointer border ${
                                hearingFormData.notify_complainant
                                  ? 'bg-blue-50 border-blue-300'
                                  : 'bg-white border-gray-200'
                              }`}
                            >
                              <input
                                type='checkbox'
                                name='notify_complainant'
                                checked={hearingFormData.notify_complainant}
                                onChange={(e) =>
                                  setHearingFormData((prev) => ({
                                    ...prev,
                                    notify_complainant: e.target.checked,
                                  }))
                                }
                                className='h-4 w-4 text-blue-600 focus:ring-blue-500 rounded'
                              />
                              <div className='ml-4'>
                                <span className='text-sm font-medium block leading-none'>
                                  {dispute.complainant_name || 'Complainant'}
                                </span>
                                <span className='text-xs text-gray-500 leading-none'>
                                  Complainant
                                </span>
                              </div>
                            </label>

                            <label
                              className={`flex items-center p-3 rounded-md cursor-pointer border ${
                                hearingFormData.notify_respondent
                                  ? 'bg-blue-50 border-blue-300'
                                  : 'bg-white border-gray-200'
                              }`}
                            >
                              <input
                                type='checkbox'
                                name='notify_respondent'
                                checked={hearingFormData.notify_respondent}
                                onChange={(e) =>
                                  setHearingFormData((prev) => ({
                                    ...prev,
                                    notify_respondent: e.target.checked,
                                  }))
                                }
                                className='h-4 w-4 text-blue-600 focus:ring-blue-500 rounded'
                              />
                              <div className='ml-4 '>
                                <span className='text-sm font-medium block leading-none'>
                                  {dispute.respondent_name || 'Respondent'}
                                </span>
                                <span className='text-xs text-gray-500 leading-none'>
                                  Respondent
                                </span>
                              </div>
                            </label>
                          </div>

                          {/* Custom Message */}
                          <div className='mt-4'>
                            <label className='block text-gray-700 text-xs uppercase font-medium mb-2'>
                              Custom Message (Optional)
                            </label>
                            <div className='relative'>
                              <textarea
                                name='custom_message'
                                value={hearingFormData.custom_message}
                                onChange={handleHearingFormChange}
                                className='w-full border border-gray-300 rounded-lg px-3 py-2 '
                                rows='6'
                                placeholder='Any additional message to include in the notification...'
                              ></textarea>
                              <div className='absolute bottom-2 right-2 text-xs text-gray-500'>
                                {hearingFormData.custom_message.length}/160
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Form Controls */}
                <div className='px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3 border-t border-gray-200'>
                  <button
                    type='button'
                    onClick={() => setShowScheduleHearingModal(false)}
                    className='px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200'
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    className='px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-sm flex items-center'
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <svg
                          className='animate-spin -ml-1 mr-2 h-4 w-4 text-white'
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                        >
                          <circle
                            className='opacity-25'
                            cx='12'
                            cy='12'
                            r='10'
                            stroke='currentColor'
                            strokeWidth='4'
                          ></circle>
                          <path
                            className='opacity-75'
                            fill='currentColor'
                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                          ></path>
                        </svg>
                        Scheduling...
                      </>
                    ) : (
                      <>
                        <CalendarIcon className='h-5 w-5 mr-2' />
                        Schedule Hearing
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Add Resolution Modal - Redesigned */}
        {showAddResolutionModal && (
          <div className='fixed inset-0 bg-black bg-opacity-60 flex items-start justify-center z-50 p-4 pt-16 overflow-y-auto'>
            <div className='bg-white rounded-xl shadow-xl w-full max-w-xl animate-fade-in-down'>
              {/* Header with gradient background */}
              <div className='bg-gradient-to-r from-green-600 to-green-800 px-6 py-4 rounded-t-xl'>
                <div className='flex justify-between items-center'>
                  <div className='flex items-center space-x-3'>
                    <div className='bg-white bg-opacity-20 p-2 rounded-lg'>
                      <CheckBadgeIcon className='h-6 w-6 text-white' />
                    </div>
                    <h2 className='text-xl font-semibold text-white'>
                      Resolve and Close Case
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowAddResolutionModal(false)}
                    className='text-white hover:bg-white hover:bg-opacity-20 p-1.5 rounded-full focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 transition-all duration-200'
                    aria-label='Close modal'
                  >
                    <XMarkIcon className='h-6 w-6' />
                  </button>
                </div>
                <p className='text-green-100 mt-2 pl-11'>
                  Case No.{' '}
                  <span className='font-medium text-white'>
                    {dispute.case_number}
                  </span>{' '}
                  •{dispute.complainant_name} vs. {dispute.respondent_name}
                </p>
              </div>

              <form onSubmit={handleSubmitResolution}>
                {/* Error message alert */}
                {errors.form && (
                  <div className='mx-6 mt-4 p-3 bg-red-100 border-l-4 border-red-500 text-red-700 rounded-r-lg flex items-start'>
                    <ExclamationCircleIcon className='h-5 w-5 mr-2 mt-0.5 flex-shrink-0' />
                    <span>{errors.form}</span>
                  </div>
                )}

                <div className='p-6 grid grid-cols-1 md:grid-cols-2 gap-6'>
                  {/* Left column - Main resolution details */}
                  <div className='space-y-5'>
                    {/* Resolution Type with icon indicators */}
                    <div>
                      <label className='flex items-center text-gray-700 text-sm font-medium mb-2'>
                        <span>Resolution Type</span>
                        <span className='text-red-500 ml-1'>*</span>
                      </label>
                      <div
                        className={`relative rounded-md shadow-sm ${
                          errors.type ? 'ring-1 ring-red-500' : ''
                        }`}
                      >
                        <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                          <ScaleIcon className='h-5 w-5 text-gray-400' />
                        </div>
                        <select
                          name='type'
                          value={resolutionFormData.type}
                          onChange={handleResolutionFormChange}
                          className='block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500'
                          required
                        >
                          <option value='settlement'>
                            Amicable Settlement
                          </option>
                          <option value='arbitration'>Arbitration Award</option>
                          <option value='withdrawal'>
                            Complaint Withdrawal
                          </option>
                          <option value='dismissal'>Dismissal</option>
                          <option value='certification'>
                            Certification to File Action
                          </option>
                        </select>
                      </div>
                      {errors.type && (
                        <p className='text-red-500 text-xs mt-1'>
                          {errors.type}
                        </p>
                      )}
                      <p className='text-xs text-gray-500 mt-1 pl-1'>
                        {resolutionFormData.type === 'settlement' &&
                          'Parties agreed on a mutual resolution'}
                        {resolutionFormData.type === 'arbitration' &&
                          'Resolution decided by Lupong Tagapamayapa'}
                        {resolutionFormData.type === 'withdrawal' &&
                          'Complainant formally withdrew the case'}
                        {resolutionFormData.type === 'dismissal' &&
                          'Case dismissed due to lack of merit or jurisdiction'}
                        {resolutionFormData.type === 'certification' &&
                          'Case to proceed to appropriate court'}
                      </p>
                    </div>

                    {/* Resolution Date with calendar icon */}
                    <div>
                      <label className='flex items-center text-gray-700 text-sm font-medium mb-1'>
                        <span>Resolution Date</span>
                        <span className='text-red-500 ml-1'>*</span>
                      </label>
                      <div
                        className={`flex rounded-md shadow-sm ${
                          errors.date ? 'ring-1 ring-red-500' : ''
                        }`}
                      >
                        <div className='bg-gray-50 px-3 py-2 border border-r-0 border-gray-300 rounded-l-md flex items-center'>
                          <CalendarDaysIcon className='h-5 w-5 text-gray-400' />
                        </div>
                        <input
                          type='date'
                          name='date'
                          value={resolutionFormData.date}
                          onChange={handleResolutionFormChange}
                          max={new Date().toISOString().split('T')[0]}
                          className='flex-1 block w-full border border-gray-300 rounded-r-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500'
                          required
                        />
                      </div>
                      {errors.date && (
                        <p className='text-red-500 text-xs mt-1'>
                          {errors.date}
                        </p>
                      )}
                    </div>

                    {/* Resolution Details textarea */}
                    <div>
                      <label className='flex items-center text-gray-700 text-sm font-medium mb-1'>
                        <span>Resolution Details</span>
                        <span className='text-red-500 ml-1'>*</span>
                      </label>
                      <div className='mt-1 relative'>
                        <div className='absolute top-2 left-2 bg-green-50 p-1.5 rounded'>
                          <DocumentTextIcon className='h-4 w-4 text-green-500' />
                        </div>
                        <textarea
                          name='details'
                          value={resolutionFormData.details}
                          onChange={handleResolutionFormChange}
                          className={`w-full border ${
                            errors.details
                              ? 'border-red-500'
                              : 'border-gray-300'
                          } rounded-lg px-3 py-2 pl-9 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 shadow-sm`}
                          rows='5'
                          placeholder='Provide full details about the resolution...'
                          required
                        ></textarea>
                        {errors.details && (
                          <p className='text-red-500 text-xs mt-1'>
                            {errors.details}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Conditional Agreement Terms */}
                    {(resolutionFormData.type === 'settlement' ||
                      resolutionFormData.type === 'arbitration') && (
                      <div className='bg-green-50 p-4 rounded-lg border border-green-100'>
                        <label className='flex items-center text-gray-700 text-sm font-medium mb-1'>
                          <CheckCircleIcon className='h-5 w-5 text-green-500 mr-1' />
                          <span>Agreement Terms</span>
                        </label>
                        <textarea
                          name='terms'
                          value={resolutionFormData.terms}
                          onChange={handleResolutionFormChange}
                          className='w-full border border-gray-300 bg-white rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 shadow-sm'
                          rows='4'
                          placeholder='Enter the specific terms of the settlement or award...'
                        ></textarea>
                        <p className='text-xs text-green-700 mt-1 pl-1'>
                          Include all agreed-upon terms, conditions, and
                          compliance requirements
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Right column - Document upload and case status */}
                  <div className='space-y-5'>
                    <div className='bg-green-50 rounded-lg border border-green-100 p-4'>
                      <h3 className='font-medium text-green-800 mb-3 flex items-center'>
                        <PaperClipIcon className='h-5 w-5 mr-2 text-green-600' />
                        Resolution Document
                      </h3>

                      {!resolutionFileSelected ? (
                        <div className='mt-1 flex flex-col justify-center items-center p-5 border-2 border-green-200 border-dashed rounded-lg bg-white cursor-pointer hover:bg-green-50 transition-colors duration-200'>
                          <div className='space-y-2 text-center'>
                            <DocumentArrowDownIcon className='mx-auto h-12 w-12 text-green-300' />
                            <div>
                              <label
                                htmlFor='resolution-file-upload'
                                className='relative cursor-pointer bg-green-100 rounded-md font-medium text-green-700 hover:text-green-900 px-4 py-2 inline-flex items-center shadow-sm hover:bg-green-200 transition-colors duration-200 focus-within:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                              >
                                <PaperClipIcon className='h-4 w-4 mr-2' />
                                <span>Select Document</span>
                                <input
                                  id='resolution-file-upload'
                                  name='document_file'
                                  type='file'
                                  onChange={handleResolutionFileChange}
                                  className='sr-only'
                                />
                              </label>
                            </div>
                            <p className='text-xs text-gray-500 mt-1'>
                              PDF document up to 10MB
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className='bg-white p-4 rounded-lg border border-green-100 relative'>
                          <button
                            type='button'
                            onClick={handleRemoveResolutionFile}
                            className='absolute top-2 right-2 text-red-500 hover:text-red-700 bg-white rounded-full p-1 shadow-sm hover:bg-red-50 transition-colors duration-200'
                            aria-label='Remove file'
                          >
                            <XMarkIcon className='h-5 w-5' />
                          </button>

                          <div className='flex flex-col items-center py-3 space-y-3'>
                            <div className='bg-green-50 p-3 rounded-full'>
                              <DocumentArrowDownIcon className='h-10 w-10 text-green-500' />
                            </div>

                            <div className='text-center'>
                              <p className='font-medium text-gray-900 break-all max-w-full'>
                                {resolutionFileSelected.name}
                              </p>
                              <p className='text-sm text-gray-500 mt-1'>
                                {(resolutionFileSelected.size / 1024).toFixed(
                                  2
                                )}{' '}
                                KB •{' '}
                                {resolutionFileSelected.type || 'Unknown Type'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Case status info box */}
                    <div className='bg-amber-50 rounded-lg p-4 border border-amber-200'>
                      <h3 className='font-medium text-amber-800 mb-2 flex items-center'>
                        <InformationCircleIcon className='h-5 w-5 mr-2 text-amber-500' />
                        Case Status Change
                      </h3>
                      <p className='text-sm text-amber-700'>
                        Adding this resolution will mark this case as{' '}
                        <span className='font-semibold'>CLOSED</span>. The case
                        will be archived and no further updates will be allowed
                        without admin permission.
                      </p>

                      <div className='mt-3 flex items-center space-x-2'>
                        <CheckCircleIcon className='h-5 w-5 text-green-500' />
                        <span className='text-sm text-gray-700'>
                          Case documents will remain accessible
                        </span>
                      </div>
                      <div className='mt-1 flex items-center space-x-2'>
                        <CheckCircleIcon className='h-5 w-5 text-green-500' />
                        <span className='text-sm text-gray-700'>
                          Case history and timeline preserved
                        </span>
                      </div>
                      <div className='mt-1 flex items-center space-x-2'>
                        <CheckCircleIcon className='h-5 w-5 text-green-500' />
                        <span className='text-sm text-gray-700'>
                          Resolution details added to case record
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Controls */}
                <div className='px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3 border-t border-gray-200'>
                  <button
                    type='button'
                    onClick={() => setShowAddResolutionModal(false)}
                    className='px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200'
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    className='px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 shadow-sm flex items-center'
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <svg
                          className='animate-spin -ml-1 mr-2 h-4 w-4 text-white'
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                        >
                          <circle
                            className='opacity-25'
                            cx='12'
                            cy='12'
                            r='10'
                            stroke='currentColor'
                            strokeWidth='4'
                          ></circle>
                          <path
                            className='opacity-75'
                            fill='currentColor'
                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                          ></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckBadgeIcon className='h-5 w-5 mr-2' />
                        Close Case with Resolution
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        <LuponMembersManagement
          dispute={dispute}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      </div>
    </div>
  );
};

// CSS for hiding scrollbar but keeping functionality
const hideScrollbarStyle = `
  .hide-scrollbar {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
  }
  .hide-scrollbar::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`;

// Export with React.memo for performance optimization to prevent unnecessary re-renders
export default React.memo(DisputeDetailsModal);
